import * as React from 'react';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import CircleIcon from '@mui/icons-material/Circle';

const data = [
  { label: 'MSR', value: 125 },
  { label: 'MSR #2', value: 125 },
  { label: 'C3', value: 125 },
  { label: 'C3 #2', value: 125 },
  { label: 'Kangaroo', value: 100 },
];

const fleetTypes = [
  {
    name: 'MSR',
    value: 20.83,
    flag: <CircleIcon sx={{ color: '#f50057' }} />,
    color: '#f50057',
  },
  {
    name: 'MSR #2',
    value: 20.83,
    flag: <CircleIcon sx={{ color: '#2979ff' }} />,
    color: '#2979ff',
  },
  {
    name: 'C3',
    value: 20.83,
    flag: <CircleIcon sx={{ color: '#000000' }} />,
    color: '#000000',
  },
  {
    name: 'C3 #2',
    value: 20.83,
    flag: <CircleIcon sx={{ color: '#4D4D4D' }} />,
    color: '#4D4D4D',
  },
  {
    name: 'Kangaroo',
    value: 16.67,
    flag: <CircleIcon sx={{ color: '#CECECE' }} />,
    color: '#CECECE',
  },
];

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const colors = [
  '#f50057',
  '#2979ff',
  '#000000',
  '#4D4D4D',
  '#CECECE'
];

export default function ChartFleetsByType() {
  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Fleets distribution
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={colors}
            margin={{
              left: 80,
              right: 80,
              top: 80,
              bottom: 80,
            }}
            series={[
              {
                data,
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { faded: 'global', highlighted: 'item' },
              },
            ]}
            height={260}
            width={260}
            slotProps={{
              legend: { hidden: true },
            }}
          >
            <PieCenterLabel primaryText="600" secondaryText="Total" />
          </PieChart>
        </Box>
        {fleetTypes.map((packType, index) => (
          <Stack
            key={index}
            direction="row"
            sx={{ alignItems: 'center', gap: 2, pb: 2 }}
          >
            {packType.flag}
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {packType.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {packType.value}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label="Number of users by country"
                value={packType.value}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: packType.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}
